<div class="container-fluid px-2 px-md-3 px-xl-1 py-3">
  <div class="d-flex flex-column gap-3">
    <section class="bg-white rounded-4 border shadow-sm p-4">
      <div class="d-flex align-items-start align-items-lg-center justify-content-between gap-3 flex-wrap mb-3">
        <div class="min-w-0">
          <div class="entity-section-title fw-bold fs-6">Question Assignments</div>
          <div class="text-secondary text-xs">
            Single-select and multi-select questionnaire items displayed with the same table styling used in risk framework setup.
          </div>
        </div>

        <div class="d-flex align-items-center gap-2 ms-auto">
          <button type="button" class="btn entity-assign-btn btn-sm rounded-small fw-semibold px-2 py-1 mt-1"
            :disabled="!questions.length || questions_loading || assignments_loading || assignments_saving"
            @click="all_questions_selected ? clear_all_questions() : select_all_questions()">
            <span v-if="all_questions_selected">Clear All</span>
            <span v-else>Select All</span>
          </button>
          <button type="button" class="btn entity-assign-btn btn-sm rounded-small fw-semibold px-2 py-1 mt-1"
            :disabled="!selected_count || questions_loading || assignments_loading || assignments_saving"
            @click="assign_selected_questions">
            {{ assignments_saving ? 'Assigning...' : 'Assign Selected Questions' }}
          </button>
         
        </div>
      </div>

      <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2 mb-3">
        <div class="d-flex align-items-center gap-2">
          <label class="text-secondary text-xs mb-0" for="question_entries">Show</label>
          <select id="question_entries" v-model.number="page_size"
            class="form-select form-select-sm rounded-3 text-xs w-auto flex-shrink-0">
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="25">25</option>
          </select>
          <span class="text-secondary text-xs">entries per page</span>
        </div>

        <div class="input-group config-search">
          <span class="input-group-text bg-light border-0 rounded-start-pill">
            <i class="bi bi-search text-secondary"></i>
          </span>
          <input v-model.trim="search_query" type="text" class="form-control form-control-sm bg-light border-0 rounded-end-pill text-xs"
            placeholder="Search questions" />
        </div>
      </div>

      <div class="entity-table-head secondary-color d-none d-lg-block rounded-3 mb-2 px-3 py-2 text-uppercase tracking-wide">
        <div class="row g-2 align-items-center">
          <div class="col-lg-2">No.</div>
          <div class="col-lg-5">Question</div>
          <div class="col-lg-2">Type</div>
          <div class="col-lg-2">Choices</div>
          <div class="col-lg-1">Actions</div>
        </div>
      </div>

      <div v-if="questions_loading" class="bg-light rounded-4 border p-4 text-center">
        <div class="fw-semibold text-xs mb-1">Loading questions...</div>
        <div class="text-secondary text-xxs">Please wait while multi-select questions are loaded.</div>
      </div>

      <div v-else-if="!paginated_questions.length" class="bg-light rounded-4 border p-4 text-center">
        <div class="fw-semibold text-xs mb-1">No matching questions found</div>
        <div class="text-secondary text-xxs">Try a different search term or reset the page size.</div>
      </div>

      <div v-else class="d-flex flex-column gap-2">
        <article v-for="(question, index) in paginated_questions" :key="question.id"
          class="entity-row-card bg-white rounded-4 border shadow-sm p-3">
          <div class="entity-row-layout row g-2 align-items-start align-items-lg-center">
            <div class="col-12 col-lg-2">
              <div class="sla-cell-label text-uppercase text-xxs fw-bold text-secondary d-lg-none mb-1">No.</div>
              <span class="text-xs fw-semibold text-secondary">{{ index + 1 }}</span>
            </div>

            <div class="col-12 col-lg-5">
              <div class="sla-cell-label text-uppercase text-xxs fw-bold text-secondary d-lg-none mb-1">Question</div>
              <span class="fw-semibold text-xs text-dark d-block">{{ question.question }}</span>
            </div>

            <div class="col-12 col-sm-6 col-lg-2">
              <div class="sla-cell-label text-uppercase text-xxs fw-bold text-secondary d-lg-none mb-1">Type</div>
              <span class="badge rounded-pill bg-light text-dark border text-xxs">{{ question.type }}</span>
            </div>

            <div class="col-12 col-sm-6 col-lg-2">
              <div class="sla-cell-label text-uppercase text-xxs fw-bold text-secondary d-lg-none mb-1">Choices</div>
              <span class="text-secondary text-xs d-block">{{ question.choices }}</span>
            </div>

            <div class="col-12 col-lg-1 d-flex justify-content-lg-end">
              <div class="sla-cell-label text-uppercase text-xxs fw-bold text-secondary d-lg-none mb-1">Actions</div>
              <div class="form-check">
                <input
                  :id="`question_${question.id}`"
                  class="form-check-input"
                  type="checkbox"
                  :checked="is_selected(question.id)"
                  @change="toggle_question(question.id)"
/>
                <label class="form-check-label text-xs" :for="`question_${question.id}`">
                  Select
                </label>
              </div>
            </div>
          </div>
        </article>
      </div>

      <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2 px-2 pt-3">
        <div class="text-secondary text-xs">{{ pagination_label }}</div>

        <nav aria-label="Question assignment pagination">
          <div class="d-inline-flex align-items-center rounded-3 border overflow-hidden bg-white">
            <button type="button" class="btn btn-sm btn-light border-0 rounded-0 px-2" @click="set_page(current_page - 1)"
              :disabled="current_page === 1" aria-label="Previous page">
              <i class="bi bi-chevron-left"></i>
            </button>

            <button v-for="page in total_pages" :key="page" type="button"
              class="btn btn-sm rounded-0 border-0 border-start px-3 pagination-seg-btn"
              :class="page === current_page ? 'btn-primary-dark text-white' : 'btn-light'" @click="set_page(page)">
              {{ page }}
            </button>

            <button type="button" class="btn btn-sm btn-light border-0 border-start rounded-0 px-2"
              @click="set_page(current_page + 1)" :disabled="current_page === total_pages" aria-label="Next page">
              <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </nav>
      </div>
    </section>
  </div>
</div>
